import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Link } from '@/components/atoms';
import { IconButton } from '@/components/atoms';
import { Photo } from '@/types/photo';
import { useActivityTracking } from '@/hooks/useActivityTracking';

export interface PhotoCardProps {
  photo: Photo;
  userSlug: string;
  onDelete?: (photoId: string) => void;
  isDeleting?: boolean;
}

export function PhotoCard({
  photo,
  userSlug,
  onDelete,
  isDeleting = false,
}: PhotoCardProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { ref: lazyLoadRef, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Activity tracking hook
  const { ref: activityTrackingRef } = useActivityTracking({
    activityType: 'view',
    entityType: 'photo',
    entityId: photo.id, // Global ID (post-045-frontend-type-id-cleanup)
    metadata: {
      source: 'photo_card',
    },
  });

  // Merge refs callback
  const mergedRef = (element: HTMLAnchorElement | null) => {
    // Set both refs
    if (typeof lazyLoadRef === 'function') {
      lazyLoadRef(element);
    } else if (lazyLoadRef) {
      (lazyLoadRef as React.MutableRefObject<HTMLAnchorElement | null>).current = element;
    }

    if (activityTrackingRef && element) {
      (activityTrackingRef as React.MutableRefObject<HTMLElement | null>).current = element;
    }
  };

  // Helper to get best URL (publicUrl preferred, storageUrl fallback) - Feature 077
  const getBestUrl = (variant: { publicUrl?: string | null; storageUrl: string } | undefined) => {
    if (!variant) return '';
    return variant.publicUrl || variant.storageUrl;
  };

  const getThumbnailSrc = () => {
    if (photo.variants && photo.variants.length > 0) {
      const thumbnail = photo.variants.find(v => v.variantType === 'thumbnail');
      if (thumbnail) return getBestUrl(thumbnail);
      const small = photo.variants.find(v => v.variantType === 'small');
      if (small) return getBestUrl(small);
      return getBestUrl(photo.variants[0]);
    }
    return '';
  };

  const buildSrcSet = () => {
    if (!photo.variants || photo.variants.length === 0) return undefined;

    return photo.variants
      .filter(v => (v.publicUrl || v.storageUrl) && v.width)
      .map(v => `${getBestUrl(v)} ${v.width}w`)
      .join(', ');
  };

  // Get dimensions from thumbnail variant or fall back to original dimensions
  const thumbnailVariant = photo.variants?.find(v => v.variantType === 'thumbnail');
  const aspectRatio = thumbnailVariant?.width && thumbnailVariant?.height
    ? `${thumbnailVariant.width} / ${thumbnailVariant.height}`
    : photo.width && photo.height
      ? `${photo.width} / ${photo.height}`
      : '1';

  const photoUrl = `/${userSlug}/photos/${photo.slug}`;

  return (
    <Link
      to={photoUrl}
      ref={mergedRef}
      className="relative block bg-surface-1 rounded-lg overflow-hidden hover:scale-[1.02] transition-transform duration-200"
      style={{ aspectRatio }}
    >
      {inView ? (
        <img
          src={getThumbnailSrc()}
          srcSet={buildSrcSet()}
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
          alt={photo.title}
          loading="lazy"
          className="w-full h-full object-contain"
        />
      ) : (
        <div className="w-full h-full bg-muted/20" />
      )}

      {/* Title Overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-background/70 to-transparent">
        <span className="text-foreground text-sm font-bold">{photo.title}</span>
      </div>

      {/* Delete Button */}
      {onDelete && (
        <div className="absolute top-2 right-2">
          <IconButton
            icon="✕"
            variant="primary"
            size="sm"
            aria-label="Delete photo"
            className="bg-danger hover:bg-danger/90"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              setShowDeleteConfirm(true);
            }}
            disabled={isDeleting}
          />
        </div>
      )}

      {/* Delete Confirmation Overlay */}
      {showDeleteConfirm && (
        <div
          className="absolute inset-0 bg-overlay/80 flex flex-col items-center justify-center gap-3 p-5 z-20"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <span className="text-foreground text-sm text-center">Delete this photo?</span>
          <div className="flex gap-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete?.(photo.id);
                setShowDeleteConfirm(false);
              }}
              className="px-4 py-2 bg-danger hover:bg-danger/90 text-primary-foreground rounded font-bold"
            >
              Delete
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowDeleteConfirm(false);
              }}
              className="px-4 py-2 bg-background hover:bg-muted/20 text-foreground rounded font-bold"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Link>
  );
}
